import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from "@angular/core";
import { first } from "rxjs/operators";
import { CandidateListService } from "src/app/shared";

@Component({
  selector: "cand-verification-data",
  templateUrl: "./cand-verification-data.component.html",
  styleUrls: ["./cand-verification-data.component.scss"]
})
export class CandVerificationDataComponent implements OnChanges, OnDestroy {
  @Input() candidateid: string;
  @Input() metricType: string;
  @Input() batchid:string;

  loading:any = false;
  imageSrc: string;
  rawData: string;
  downloadHref: string;
  downloadObjectUrl: string;
  downloadName: string;
  byteSize: number;
  model: any  = {};
  reject_type:any = "rejected";
  constructor(private candidateService:CandidateListService){

  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.candidateid || changes.metricType) {
      this.loadData ();
    }
  }

  ngOnDestroy() {
    this.revokeDownloadUrl();
  }

  private loadData (){
    this.revokeDownloadUrl();
    this.loading = true;
    this.imageSrc = "";
    this.rawData = "";
    this.downloadHref = "";
    this.downloadObjectUrl = "";
    this.downloadName = this.getDownloadName();
    this.byteSize = 0;
    this.candidateService.getVerificationMetricData (this.candidateid, this.metricType, this.batchid)
    .pipe (first())
    .subscribe(res=>{
      this.model  = res;
      this.rawData = this.model.metric_data || "";
      if (this.isImageMetric(this.metricType)){ 
       this.imageSrc = this.normalizeBase64(this.rawData);
       this.byteSize = this.getByteSize(this.rawData);
       if (this.rawData) {
         this.downloadHref = this.createImageDownloadUrl(this.rawData);
       }
      }else {
        this.byteSize = this.getTextSize(this.rawData);
        if (this.rawData) {
          this.downloadHref = this.createRawDownloadUrl(this.rawData);
        }
      }
      this.loading = false;
    }, () => {
      this.loading = false;
    })
  }

  private normalizeBase64(base64Data: string): string {
    if (!base64Data) {
      return "";
    }

    if (base64Data.startsWith("data:")) {
      return base64Data;
    }

    return `data:image/png;base64,${base64Data}`;
  }

  private getDownloadName(): string {
    const candidateId = this.candidateid || "candidate";
    const batchId = this.batchid || "batch";
    const metricType = this.metricType || "verification";
    const extension = this.isImageMetric(metricType) ? "png" : "txt";
    return `${candidateId}-${batchId}-${metricType}.${extension}`;
  }

  private isImageMetric(metricType: string): boolean {
    return !!metricType && metricType !== this.reject_type && !metricType.toUpperCase().endsWith("_ISO");
  }

  private createImageDownloadUrl(base64Data: string): string {
    const bytes = this.getByteArray(base64Data);
    this.downloadObjectUrl = URL.createObjectURL(new Blob([bytes], { type: "image/png" }));
    return this.downloadObjectUrl;
  }

  private createRawDownloadUrl(rawData: string): string {
    this.downloadObjectUrl = URL.createObjectURL(
      new Blob([rawData || ""], { type: "text/plain;charset=utf-8" })
    );
    return this.downloadObjectUrl;
  }

  private getByteSize(base64Data: string): number {
    return this.getByteArray(base64Data).length;
  }

  private getByteArray(base64Data: string): Uint8Array {
    if (!base64Data) {
      return new Uint8Array(0);
    }

    const content = this.getBase64Content(base64Data);
    const binary = atob(content);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

  private getBase64Content(base64Data: string): string {
    const normalizedData = base64Data.trim();
    const dataParts = normalizedData.split(",");
    return normalizedData.startsWith("data:") ? dataParts[dataParts.length - 1] : normalizedData;
  }

  private getTextSize(value: string): number {
    return new Blob([value || ""], { type: "text/plain;charset=utf-8" }).size;
  }

  onDownload(): void {
    if (!this.downloadHref) {
      return;
    }

    const link = document.createElement("a");
    link.href = this.downloadHref;
    link.download = this.downloadName;
    link.rel = "noopener noreferrer";
    link.click();
  }

  private revokeDownloadUrl(): void {
    if (this.downloadObjectUrl) {
      URL.revokeObjectURL(this.downloadObjectUrl);
      this.downloadObjectUrl = "";
    }
  }
}
